// ── UTFALL IN I EN PROGNOSVERSION ─────────────────────────────────
// En P-version är budgeten med de stängda månaderna utbytta mot verkligt
// utfall: "hittills så här, resten som planerat". Styrelserapporten med
// P-versionen vald blir då helårsprognosen.
//
// Bokföringen (SIE) är facit, men bara per kontogrupp. Raderna fördelas
// med bästa tillgängliga källa — Qlik per resultatenhet, kontotyp för
// övriga kostnader, kostnadsställe 110 för O/H — och en restrad per grupp
// tar skillnaden. Därmed stämmer VARJE grupp exakt mot bokföringen, och
// resultatet per månad blir bokföringens resultat. Det är kravet: en
// prognos vars utfallsmånader inte går ihop med böckerna går inte att
// visa en styrelse.

const OH_KS = '110'; // kostnadsställe för koncerngemensamt i bokföringen

// Alla rader som hör till resultaträkningen (likvidraderna rörs aldrig)
const RR_GRUPPER = new Set(['intakt', 'vk', 'ovrigt', 'personal', 'avskr', 'finans']);

/**
 * @param {object} p
 * @param {string} p.bolag            'hotell' | 'sc' | 'stigmyhrs'
 * @param {Array}  p.raderDef         RADER[bolag]
 * @param {Array}  p.man              månader (ym) som ska fyllas
 * @param {object} p.bok              ym → { summor, rader:[{konto,ks,belopp}] }
 * @param {object} [p.agg]            Qlik: enhet → ym → { intakt, vk, lon }
 * @param {object} [p.lanthandel]     Stigmyhrs: ym → Lanthandelns intäkt ex moms
 * @param {Function} p.kontoGrupp     konto → grupp
 * @param {Function} p.arFast         konto → true om kontot klassas som fast kostnad
 * @returns {object} radId → ym → kr, för varje RR-rad och varje månad i man
 */
export function utfallTillRader({ bolag, raderDef, man, bok, agg = {}, lanthandel = {}, kontoGrupp, arFast }) {
  const ut = {};
  const satt = (radId, ym, kr) => {
    if (!raderDef.some((r) => r.id === radId)) return;
    (ut[radId] || (ut[radId] = {}))[ym] = Math.round(kr || 0);
  };
  const qlik = (enhet, ym, falt) => ((agg[enhet] || {})[ym] || {})[falt] || 0;

  for (const ym of man) {
    const b = bok[ym];
    if (!b) continue;
    const s = b.summor || {};

    // Börja med noll på varje RR-rad, så att inget budgetvärde blir kvar
    // och förstör gruppsumman.
    for (const r of raderDef) if (RR_GRUPPER.has(r.grupp)) satt(r.id, ym, 0);

    if (bolag === 'stigmyhrs') {
      // Lanthandeln har egen kassa (HonestBox); huvudbutiken är resten av
      // bokförd intäkt. Varukostnaden delas efter intäktsandel — KS är för
      // glest kodat i det bolaget för att gå att lita på.
      const lh = lanthandel[ym] || 0;
      const hb = (s.intakt || 0) - lh;
      satt('intakt:lanthandel', ym, lh);
      satt('intakt:huvudbutik', ym, hb);
      const andel = hb + lh > 0 ? lh / (hb + lh) : 0;
      const vkLh = Math.round((s.vk || 0) * andel);
      satt('vk:lanthandel', ym, vkLh);
      satt('vk:huvudbutik', ym, (s.vk || 0) - vkLh);
    } else {
      // Intäkter och varukostnad: Qlik per enhet, restraden tar resten
      let sumI = 0, sumV = 0;
      for (const r of raderDef) {
        if (!r.qlikEnhet) continue;
        if (r.grupp === 'intakt') { const v = Math.round(qlik(r.qlikEnhet, ym, 'intakt')); satt(r.id, ym, v); sumI += v; }
        if (r.grupp === 'vk')     { const v = Math.round(qlik(r.qlikEnhet, ym, 'vk'));     satt(r.id, ym, v); sumV += v; }
      }
      satt('intakt:ovriga', ym, (s.intakt || 0) - sumI);
      satt('vk:ovriga', ym, (s.vk || 0) - sumV);
    }

    // Övriga kostnader: O/H ur kostnadsställe 110, resten delat fast/rörligt
    // per konto — samma klassning som kostnadsanalysen och generatorn.
    let oh = 0, fast = 0;
    for (const r of b.rader || []) {
      if (kontoGrupp(r.konto) !== 'ovrigt') continue;
      if (r.ks === OH_KS) oh += r.belopp;
      else if (arFast(r.konto)) fast += r.belopp;
    }
    const harOhRad = raderDef.some((r) => r.id === 'ovrigt:oh');
    if (!harOhRad) oh = 0;
    satt('ovrigt:oh', ym, oh);
    satt('ovrigt:fasta', ym, fast);
    satt('ovrigt:ovriga', ym, (s.ovrigt || 0) - oh - fast);

    // Personal: O/H-enhetens lön ur Qlik (bara Hotell har den), resten på
    // fast personal. Qlik vet inget om fast/säsong — säsongsraden nollas
    // för utfallsmånaderna i stället för att gissas.
    const ohLon = bolag === 'hotell' ? Math.round(qlik('OH', ym, 'lon')) : 0;
    satt('personal:oh', ym, ohLon);
    satt('personal:fast', ym, (s.personal || 0) - ohLon);

    satt('avskrivningar', ym, s.avskr || 0);
    // Bokföringens finans är redan netto (räntekostnader minus -intäkter),
    // så ränteintäktsraden nollas — annars dras den av två gånger.
    satt('rantor', ym, s.finans || 0);
  }
  return ut;
}

// Lägg in utfallet i budgetens rader. Månader utanför `man` och alla
// likvidrader lämnas orörda; nollor tas bort som i övriga matrisen.
export function slaIhop(rader, utfall) {
  const nya = { ...rader };
  for (const [radId, perYm] of Object.entries(utfall)) {
    const rad = { ...(nya[radId] || {}) };
    for (const [ym, kr] of Object.entries(perYm)) {
      if (kr) rad[ym] = kr; else delete rad[ym];
    }
    nya[radId] = rad;
  }
  return nya;
}
